import { Calculate, ExitToApp, Monitor, SaveAlt, Settings, TrendingDown, TrendingUp } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Grid, Paper, Slide, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import "chart.js/auto";
import { get, getDatabase, ref, set } from "firebase/database";
import { memo, useEffect, useMemo, useState } from "react";
import { Line, Scatter } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { adx, atr, rsi } from "technicalindicators";
import InViewportRenderer from "./Components/InViewportRenderer";
import ScrollButton from "./Components/ScrollButton";
import { timeframes, tradingpairs } from "./Components/TradingPairs";
import { app } from "./Components/FirebaseConfig.js";

// INITIALIZE DATABASE
const database = getDatabase(app);

// INITIALIZE KLINES DATA ARRAY
const klines_data = [];

// PLOT USING REACT-CHART-JS
const ChartJSPlot = memo(({ data }) => {
  const chart_data = {
    labels: data["open_time"],
    datasets: [
      {
        type: "scatter",
        data: data["PEAKS"],
        backgroundColor: "red",
        pointRadius: 6,
      },
      {
        type: "scatter",
        data: data["TROUGHS"],
        backgroundColor: "green",
        pointRadius: 6,
      },
      {
        type: "line",
        data: data["close_smooth"],
        borderColor: "blue",
        borderWidth: 3,
      },
      {
        type: "line",
        data: data["close"],
        borderColor: "rgba(128, 128, 128, 0.5)",
        borderWidth: 2,
      },
    ],
  };

  const options = {
    spanGaps: true,
    normalized: true,
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    elements: {
      point: {
        radius: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
  };
  return (
    <InViewportRenderer style={{ width: "100%", height: 200 }}>
      <Line data={chart_data} options={options} />
      <Scatter data={chart_data} options={options} />
    </InViewportRenderer>
  );
});

// RISK SETTINGS COMPONENT
function RiskSettingsComponent() {
  const [risk_settings, set_risk_settings] = useState({
    stoploss_risk: 1,
    risk_per_trade: 50,
  });

  const handle_risk_settings_change = (e) => {
    const { name, value } = e.target;
    set_risk_settings((prev_value) => ({
      ...prev_value,
      [name]: value,
    }));
  };

  const usdt_multiplier = useMemo(() => {
    const stoploss_risk = risk_settings.stoploss_risk && parseFloat(risk_settings.stoploss_risk) !== 0 ? risk_settings.stoploss_risk : 1;
    return (risk_settings.risk_per_trade / (stoploss_risk / 100)).toFixed(0);
  }, [risk_settings.stoploss_risk, risk_settings.risk_per_trade]);

  return (
    <Paper variant="outlined" sx={{ borderColor: "primary.main", borderWidth: 2, p: 2, pt: 1 }}>
      <Grid sx={{ display: "flex", alignItems: "center" }} container spacing={2}>
        <Grid item sx={{ display: "flex", alignItems: "center" }} xs={12}>
          <Calculate sx={{ color: "primary.main", mr: 0.5 }} />
          <Typography component="h1" variant="h6" sx={{ letterSpacing: 2, color: "primary.main", userSelect: "none" }}>
            RISK MANAGEMENT
          </Typography>
        </Grid>
        <Grid item xs={6} md={4} xl={4}>
          <TextField
            error={!risk_settings.stoploss_risk}
            fullWidth
            size="small"
            label="STOPLOSS RISK (%)"
            variant="outlined"
            type="number"
            name="stoploss_risk"
            inputProps={{ step: "0.5" }}
            value={risk_settings.stoploss_risk}
            onChange={handle_risk_settings_change}
          />
        </Grid>
        <Grid item xs={6} md={4} xl={4}>
          <TextField
            fullWidth
            error={!risk_settings.risk_per_trade}
            size="small"
            label="RISK PER TRADE ($)"
            variant="outlined"
            type="number"
            name="risk_per_trade"
            inputProps={{ step: "0.5" }}
            value={risk_settings.risk_per_trade}
            onChange={handle_risk_settings_change}
          />
        </Grid>
        <Grid item xs={12} md={4} xl={4}>
          <TextField
            focused
            fullWidth
            size="small"
            label="USDT MULTIPLIER ($)"
            variant="outlined"
            type="number"
            name="usdt_multiplier"
            inputProps={{ readOnly: true }}
            sx={{
              "& label.Mui-focused": {
                color: "#082958",
              },
              "& label": {
                color: "#082958",
              },
              "& .MuiOutlinedInput-root": {
                color: "#082958",
                "& fieldset": {
                  borderColor: "#082958",
                },
                "&:hover fieldset": {
                  borderColor: "#082958",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#082958",
                },
              },
            }}
            value={usdt_multiplier}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

// SETTINGS COMPONENT
function SettingsComponent({ init_settings, set_init_settings }) {
  const [settings, set_settings] = useState(init_settings);
  const [is_submitted, set_submitted] = useState(false);
  const [alert_type, set_alert_type] = useState({});

  const handle_settings_change = (e) => {
    const { name, value } = e.target;
    set_settings((prev_value) => ({ ...prev_value, [name]: value }));
  };

  const handle_settings_submit = (e) => {
    e.preventDefault();
    let is_valid = true;
    for (const value_setting of Object.values(settings)) {
      if (!value_setting) {
        is_valid = false;
        set_alert_type({
          severity: "error",
          message: "Some required fields are missing.",
        });
        set_submitted(true);
        break;
      }
    }
    if (is_valid) {
      set(ref(database, "/settings"), settings);
      set_init_settings(settings);
      set_alert_type({
        severity: "success",
        message: "Settings saved successfully!",
      });
      set_submitted(true);
    }
  };

  return (
    <>
      <Paper
        sx={{ borderColor: "primary.main", borderWidth: 2, p: 2, pt: 1 }}
        variant="outlined"
        component="form"
        onSubmit={handle_settings_submit}
        noValidate
        autoComplete="off"
      >
        <Grid sx={{ display: "flex", justifyContent: "center", alignItems: "center" }} container spacing={2}>
          <Grid item sx={{ display: "flex", alignItems: "center" }} xs={12}>
            <Settings sx={{ color: "primary.main", mr: 0.5 }} />
            <Typography component="h1" variant="h6" sx={{ letterSpacing: 2, color: "primary.main", userSelect: "none" }}>
              SETTINGS
            </Typography>
          </Grid>
          <Grid item xs={4} md={2} xl={2}>
            <TextField
              error={!settings.lookback}
              fullWidth
              size="small"
              label="LOOKBACK"
              variant="outlined"
              type="number"
              name="lookback"
              value={settings.lookback}
              onChange={handle_settings_change}
              required
            />
          </Grid>
          <Grid item xs={4} md={2} xl={2}>
            <TextField
              error={!settings.peaks_distance}
              fullWidth
              size="small"
              label="PEAKS DISTANCE"
              variant="outlined"
              type="number"
              name="peaks_distance"
              value={settings.peaks_distance}
              onChange={handle_settings_change}
              required
            />
          </Grid>
          <Grid item xs={4} md={2} xl={2}>
            <TextField
              error={!settings.peaks_width}
              fullWidth
              size="small"
              label="PEAKS WIDTH"
              variant="outlined"
              type="number"
              name="peaks_width"
              value={settings.peaks_width}
              onChange={handle_settings_change}
              required
            />
          </Grid>
          <Grid item xs={4} md={2} xl={2}>
            <TextField
              error={!settings.window_length}
              fullWidth
              size="small"
              label="WINDOW LENGTH"
              variant="outlined"
              type="number"
              name="window_length"
              value={settings.window_length}
              onChange={handle_settings_change}
              required
            />
          </Grid>
          <Grid item xs={4} md={2} xl={2}>
            <TextField
              error={!settings.polyorder}
              fullWidth
              size="small"
              label="POLYORDER"
              variant="outlined"
              type="number"
              name="polyorder"
              value={settings.polyorder}
              onChange={handle_settings_change}
              required
            />
          </Grid>
          <Grid item xs={4} md={2} xl={2}>
            <Button startIcon={<SaveAlt />} color="primary" fullWidth type="submit" variant="contained" disableElevation>
              SAVE
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Snackbar open={is_submitted} autoHideDuration={3000} onClose={() => set_submitted(false)}>
        <Alert
          sx={{ position: "fixed", bottom: 40, left: 5 }}
          onClose={() => set_submitted(false)}
          variant="filled"
          severity={alert_type["severity"]}
        >
          {alert_type["message"]}
        </Alert>
      </Snackbar>
    </>
  );
}

// TIMER COMPONENT
let get_current_index;
const TimerComponent = ({ index_data }) => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (index_data === get_current_index) setSeconds(0);
      else setSeconds((prevSeconds) => prevSeconds + 1);
    }, 1000);

    // Cleanup the interval when the component is unmounted
    return () => {
      clearInterval(intervalId);
    };
  }, [index_data]); // Empty dependency array ensures the effect runs only once on mount

  // Convert total seconds to hours, minutes, and remaining seconds
  // const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0) return <>{minutes}M AGO</>;
  return <>{remainingSeconds}S AGO</>;
};

// RENDER KLINES COMPONENT
const RenderKlinesComponent = memo(({ bgcolor, value_data, index_data }) => {
  return (
    <Box>
      <Box
        sx={{
          p: 3.5,
          position: "relative",
          display: "flex",
          alignItems: "center",
          color: "primary.text",
          bgcolor: bgcolor,
        }}
      >
        <Tooltip id={index_data} arrow placement="top" title={`Go to Binance Futures ${value_data["symbol"].toUpperCase()}`}>
          <Typography
            sx={{
              position: "absolute",
              left: 10,
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "primary.text",
              fontWeight: "bold",
            }}
            component={Link}
            to={`https://www.binance.com/en/futures/${value_data["symbol"].toUpperCase()}`}
            target="_blank"
            variant="h4"
          >
            {(value_data["overall_trend"] === "uptrend" && <TrendingUp sx={{ mr: 0.5 }} />) ||
              (value_data["overall_trend"] === "downtrend" && <TrendingDown sx={{ mr: 0.5 }} />)}
            {`${value_data["symbol"]}`}
          </Typography>
        </Tooltip>
        <Typography
          sx={{ position: "absolute", left: { xs: "65%", sm: "50%" }, transform: { xs: "translate(-65%, 0)", sm: "translate(-50%, 0)" } }}
          component="h2"
          variant="h6"
        >{`$${parseFloat(value_data["timeframes"].slice(-1)[0]["klines"]["close"].slice(-1)[0]).toFixed(2)}`}</Typography>
        <Typography sx={{ position: "absolute", right: 10, userSelect: "none" }} component="h2" variant="h6">
          <TimerComponent index_data={index_data} />
        </Typography>
      </Box>
      <Box sx={{ overflow: "hidden" }}>
        <Slide sx={{ p: 1 }} in={true} direction="down" timeout={1000}>
          <Grid container spacing={1}>
            {value_data["timeframes"].map((value_tf, index_tf) => {
              if (Object.keys(value_tf).length === 0) return <></>;
              let bgcolor = "none";
              let bordercolor = "primary.main";
              if (value_tf["trend"] === "uptrend") {
                bgcolor = "rgba(0,255,0,0.2)";
                bordercolor = "green";
              } else if (value_tf["trend"] === "downtrend") {
                bgcolor = "rgba(255,0,0,0.2)";
                bordercolor = "red";
              }
              return (
                <>
                  <Grid item xs={12} md={4} xl={2}>
                    <Paper sx={{ borderColor: bordercolor, borderWidth: 2, bgcolor: bgcolor, p: 1 }} variant="outlined">
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                        <Typography
                          color="primary.main"
                          sx={{ display: "flex", alignItems: "center", userSelect: "none" }}
                          component="h1"
                          variant="h6"
                        >
                          {`${value_data["symbol"]} ${value_tf["timeframe"].toUpperCase()}`}
                        </Typography>
                        {(value_tf["trend"] === "uptrend" && <TrendingUp fontSize="large" />) ||
                          (value_tf["trend"] === "downtrend" && <TrendingDown fontSize="large" />)}
                      </Box>
                      <ChartJSPlot data={value_tf["klines"]} />
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography color="secondary.text" sx={{ userSelect: "none" }} component="h2">
                          {`ADX ${value_tf["klines"]["adx"].slice(-1)[0].toFixed(0)}`}
                        </Typography>
                        <Typography color="secondary.text" sx={{ userSelect: "none" }} component="h2">
                          {`RSI ${value_tf["klines"]["rsi"].slice(-1)[0].toFixed(0)}`}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </>
              );
            })}
          </Grid>
        </Slide>
      </Box>
    </Box>
  );
});

// FETCH KLINES COMPONENT
function FetchKlinesComponent({ on_logout, init_settings }) {
  const [current_index, set_current_index] = useState(0);
  get_current_index = current_index;

  useEffect(() => {
    let is_running = true;
    const fetch_binance_api = async () => {
      while (is_running) {
        for (const [index_symbol, key_symbol] of tradingpairs.entries()) {
          if (!is_running) break;
          const processed_timeframes = await Promise.all(
            Object.entries(timeframes).map(async ([index_timeframe, key_timeframe]) => {
              try {
                const lookback = 500;
                const klines_response = await axios.get("https://api.binance.com/api/v3/klines", {
                  params: {
                    symbol: key_symbol,
                    interval: key_timeframe,
                    limit: lookback,
                  },
                });
                const klines_transformed = klines_response.data.reduce(
                  (accumulator, kline) => {
                    accumulator.open_time.push(kline[0]);
                    accumulator.open.push(parseFloat(kline[1]));
                    accumulator.high.push(parseFloat(kline[2]));
                    accumulator.low.push(parseFloat(kline[3]));
                    accumulator.close.push(parseFloat(kline[4]));
                    return accumulator;
                  },
                  { open_time: [], open: [], high: [], low: [], close: [] }
                );
                const calculate_atr = atr({
                  high: klines_transformed["high"],
                  low: klines_transformed["low"],
                  close: klines_transformed["close"],
                  period: 14,
                });
                const calculate_rsi = rsi({
                  values: klines_transformed["close"],
                  period: 14,
                });
                const calculate_adx = adx({
                  high: klines_transformed["high"],
                  low: klines_transformed["low"],
                  close: klines_transformed["close"],
                  period: 14,
                });
                const preprocessed_response = await axios.post(
                  "https://fetch-preprocessed-crypto-data.vercel.app/api",
                  {
                    klines: klines_transformed,
                    distance: init_settings.peaks_distance,
                    width: init_settings.peaks_width,
                    window_length: init_settings.window_length,
                    polyorder: init_settings.polyorder,
                    atr: calculate_atr.slice(-init_settings.lookback)[0],
                  },
                  {
                    headers: {
                      "Content-Type": "application/json",
                      Accept: "application/json",
                    },
                  }
                );
                preprocessed_response.data["rsi"] = [...Array(lookback - calculate_rsi.length).fill(null), ...calculate_rsi];
                preprocessed_response.data["adx"] = [
                  ...Array(lookback - calculate_adx.length).fill(null),
                  ...calculate_adx.map((value, index) => calculate_adx[index].adx),
                ];
                const PEAKS = preprocessed_response.data["PEAKS"].filter((value) => Boolean(value));
                const TROUGHS = preprocessed_response.data["TROUGHS"].filter((value) => Boolean(value));
                let up_run_length = 0;
                let up_run = true;
                while (up_run) {
                  if (2 + up_run_length > PEAKS.length || 2 + up_run_length > TROUGHS.length) break;
                  if (
                    PEAKS[PEAKS.length - 1 - up_run_length] > PEAKS[PEAKS.length - 2 - up_run_length] &&
                    TROUGHS[TROUGHS.length - 1 - up_run_length] > TROUGHS[TROUGHS.length - 2 - up_run_length]
                  )
                    up_run_length++;
                  else up_run = false;
                }
                let down_run_length = 0;
                let down_run = true;
                while (down_run) {
                  if (2 + down_run_length > PEAKS.length || 2 + down_run_length > TROUGHS.length) break;
                  if (
                    PEAKS[PEAKS.length - 1 - down_run_length] < PEAKS[PEAKS.length - 2 - down_run_length] &&
                    TROUGHS[TROUGHS.length - 1 - down_run_length] < TROUGHS[TROUGHS.length - 2 - down_run_length]
                  )
                    down_run_length++;
                  else down_run = false;
                }
                let trend = null;
                if (up_run_length > down_run_length) trend = "uptrend";
                else if (up_run_length < down_run_length) trend = "downtrend";

                for (const key of Object.keys(preprocessed_response.data)) {
                  preprocessed_response.data[key] = preprocessed_response.data[key].slice(-init_settings.lookback);
                }

                return {
                  index: index_timeframe,
                  timeframe: key_timeframe,
                  klines: preprocessed_response.data,
                  trend: trend,
                };
              } catch (error) {
                console.error(error);
                return {};
              }
            })
          );

          let overall_trend = null;
          let total_uptrend = 0;
          let total_downtrend = 0;
          const sorted_tfs = processed_timeframes.sort((a, b) => a.index - b.index);
          sorted_tfs.forEach((value, index) => {
            if (value["trend"] === "uptrend") total_uptrend++;
            if (value["trend"] === "downtrend") total_downtrend++;
          });

          if (0.83 <= total_uptrend / timeframes.length) overall_trend = "uptrend";
          if (0.83 <= total_downtrend / timeframes.length) overall_trend = "downtrend";

          const processed_symbols = {
            index: index_symbol,
            symbol: key_symbol,
            timeframes: sorted_tfs,
            overall_trend: overall_trend,
          };
          let is_added = false;
          for (const [i, v] of klines_data.entries()) {
            if (index_symbol === v["index"]) {
              klines_data[i] = processed_symbols;
              is_added = true;
              break;
            }
          }
          if (!is_added) klines_data.push(processed_symbols);
          set_current_index(index_symbol);
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    };
    fetch_binance_api();
    return () => (is_running = false);
  }, [init_settings]);

  return (
    <>
      {klines_data.map((value_data, index_data) => {
        if (Object.keys(value_data).length === 0 || ("timeframes" in value_data && Object.keys(value_data["timeframes"].slice(-1)[0]).length === 0))
          return <></>;
        let bgcolor = "secondary.main";
        if (value_data["overall_trend"] === "uptrend") bgcolor = "green";
        if (value_data["overall_trend"] === "downtrend") bgcolor = "red";
        return (
          <Paper
            variant="outlined"
            sx={{
              bgcolor: index_data === current_index ? "yellow" : "none",
              borderStyle: "solid",
              borderWidth: 5,
              borderColor: bgcolor,
              borderTop: "none",
              my: 1,
            }}
          >
            <RenderKlinesComponent bgcolor={bgcolor} value_data={value_data} index_data={index_data} />
          </Paper>
        );
      })}
      <Slide in={true} direction="up" timeout={1000}>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            color: "primary.text",
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            userSelect: "none",
          }}
        >
          <Typography component="p" sx={{ p: 0.5, mx: { xs: 0, sm: "auto" } }}>
            {(klines_data.filter((value) => value["index"] === current_index).length > 0 &&
              `${klines_data.filter((value) => value["index"] === current_index)[0]["symbol"].toUpperCase()} is being updated...`) ||
              "Loading..."}
          </Typography>
          <Button
            sx={{
              position: "absolute",
              right: 0,
            }}
            endIcon={<ExitToApp />}
            size="small"
            onClick={on_logout}
            variant="contained"
            color="primary"
            disableElevation
          >
            LOGOUT
          </Button>
        </Box>
      </Slide>
    </>
  );
}

// DASHBOARD COMPONENT
export default function Dashboard({ on_logout }) {
  const [init_settings, set_init_settings] = useState(null);

  useEffect(() => {
    get(ref(database, "/settings"))
      .then((snapshot) => {
        if (snapshot.exists()) {
          set_init_settings(snapshot.val());
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  return (
    <>
      {(!init_settings && (
        <Box
          className="fadeInAnimation"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "90vh",
          }}
        >
          <CircularProgress disableShrink size={200} thickness={5} sx={{ color: "primary.main" }} />
        </Box>
      )) || (
        <Box>
          <Box sx={{ zIndex: 99, position: { xs: "static", md: "fixed" }, left: 0, top: 0, bgcolor: "white" }}>
            <Box sx={{ p: 1, color: "primary.text", bgcolor: "primary.main", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Monitor color="inherit" fontSize="medium" sx={{ mr: 0.5 }} />
              <Typography sx={{ fontWeight: "bold", userSelect: "none" }} component="h1" variant="h5">
                CRYPTO TRADING MONITOR
              </Typography>
            </Box>
            <Box sx={{ overflow: "hidden" }}>
              <Slide in={true} direction="down" timeout={1000}>
                <Box sx={{ p: 1, borderStyle: "solid", borderWidth: 5, borderColor: "primary.main", borderTop: "none" }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} xl={4}>
                      <RiskSettingsComponent />
                    </Grid>
                    <Grid item xs={12} xl={8}>
                      <SettingsComponent init_settings={init_settings} set_init_settings={set_init_settings} />
                    </Grid>
                  </Grid>
                </Box>
              </Slide>
            </Box>
          </Box>
          <Box sx={{ mt: { xs: 0, md: 40, xl: 25 } }}>
            <FetchKlinesComponent on_logout={on_logout} init_settings={init_settings} />
          </Box>
          <ScrollButton />
        </Box>
      )}
    </>
  );
}
